import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { isReadable, TinyColor } from '@ctrl/tinycolor';

import { ENVIRONMENT } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { HeaderService } from '@usheru-hff/sdk/header';
import { TRACKING_SERVICE, TrackingService } from '@usheru-hff/sdk/tracking';
import { toBoolean } from '@usheru-hff/sdk/utils';
import {
  getChannelMovieProp,
  Movie,
  getMovieGenres,
  getMovieTags,
  MovieSiteCompetitionPlacement
} from '@usheru-hff/shared/domain';
import { MovieSiteSideMenuComponent } from '@usheru-hff/core/movie-site';

@Injectable({
  providedIn: 'root'
})
export class BrandMovieService {
  private readonly BODY_CLASS_NAME = 'movie-branded-page-site';
  private readonly PLATFORM_ID = inject(PLATFORM_ID);
  private readonly isPlatformBrowser = isPlatformBrowser(this.PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly headerService = inject(HeaderService);
  private readonly channelService = inject(ChannelService);
  private readonly env = inject(ENVIRONMENT);
  private readonly document = inject(DOCUMENT);

  private readonly trackingService = inject(TRACKING_SERVICE);
  readonly movie = signal<Movie | undefined>(undefined);

  readonly desktopBreakpoint = 1200;
  readonly defaultCompetitionPlacement: MovieSiteCompetitionPlacement = 'awareness';
  private readonly competitionPlacementValues: readonly MovieSiteCompetitionPlacement[] = [
    'awareness',
    'conversion',
    'both'
  ];

  activeMovieRequest?: string;
  sidemenuRef?: MovieSiteSideMenuComponent;
  injectStylesEl?: HTMLStyleElement;
  footerRestoreRef?: {
    footer: HTMLElement;
    parent: HTMLElement;
    sibling: HTMLElement;
    insertPostion: 'after' | 'before';
  };

  /**
   * If the the user navigates again to the same movie, the resolver will not be executed again and will take the before visited movie.
   * This method checks if the movie in the route state is different from the current movie and updates the movie.
   */

  checkMovieInRouteState() {
    const state = this.router.getCurrentNavigation()?.extras.state || {};
    const movieState = state['movie'] as Movie;
    if (!movieState) {
      return;
    }

    if (movieState.id !== this.movie()?.id) {
      /**
       * Updates to the full movie object in case the movie (in the state) is not complete.
       * The result of this request should be already cached.
       * !IMPORTANT This request must be same as the one in the movie-page.resolver.ts
       */

      return;
    }
  }

  isMovieAwareness(movie: Movie): boolean {
    const today = new Date().getTime();
    if (toBoolean(movie.properties?.movieSiteAutomatedSwitch) && movie.locCinemaEnd) {
      return movie.locCinemaEnd > today;
    } else {
      return true;
    }
  }

  preSetColorStyles(movie: Movie) {
    const brandColor = movie?.properties?.theme?.mainColor || '#000000';
    const backgroundColor = movie?.properties?.theme?.backgroundColor || '#ffffff';
    const borderRadius = movie?.properties?.selectShowtimeContainerBorderRadius || '12px';
    const customBrandContrast = movie?.properties?.theme?.mainColorContrast;
    const customBackgroundContrast = movie?.properties?.theme?.backgroundColorContrast;
    return this.setColorStyles({
      brandColor,
      backgroundColor,
      borderRadius,
      customBrandContrast,
      customBackgroundContrast
    });
  }
  /**
   * Generate the colors variables and set some global styles
   * @returns a string to be used in the HostBinding style
   */

  setColorStyles(style: {
    brandColor: string;
    backgroundColor: string;
    borderRadius: string;
    customBrandContrast?: string;
    customBackgroundContrast?: string;
  }): {
    [rule: string]: string;
  } {
    const { brandColor, backgroundColor, borderRadius, customBrandContrast, customBackgroundContrast } = style;

    const getColorStyling = (args: ColorSetInput): ColorSet => {
      const { color, textWhite, textBlack } = args;
      const redeableColor = isReadable(color, textBlack) ? textBlack : textWhite;
      const hoverColor = color.isLight() ? color.darken(10) : color.lighten(10);
      const shaded = color.isLight() ? color.lighten(5) : color.darken(5);
      const shadedRedeable = isReadable(shaded, textBlack) ? textBlack : textWhite;
      const white = new TinyColor('#ffffff').darken(5);
      const black = new TinyColor('#000000').lighten(5);
      let redeableColorContrast, redeableColorContrastInverse;
      if (isReadable(redeableColor, '#000000')) {
        redeableColorContrast = white;
        redeableColorContrastInverse = black;
      } else {
        redeableColorContrast = black;
        redeableColorContrastInverse = white;
      }

      return {
        color,
        redeableColor,
        redeableColorContrast,
        redeableColorContrastInverse,
        hoverColor,
        shaded,
        shadedRedeable,
        textWhite,
        textBlack
      };
    };

    const defaultWhite = new TinyColor('white');
    const defaultBlack = new TinyColor('black');

    let customColor1Contrast, customColor2Constrast;
    if (customBrandContrast) customColor1Contrast = new TinyColor(customBrandContrast);
    if (customBackgroundContrast) customColor2Constrast = new TinyColor(customBackgroundContrast);

    const args = {
      color1: new TinyColor(brandColor),
      color2: new TinyColor(backgroundColor),
      color1TextWhite: customColor1Contrast || defaultWhite,
      color1TextBlack: customColor1Contrast || defaultBlack,
      color2TextWhite: customColor2Constrast || defaultWhite,
      color2TextBlack: customColor2Constrast || defaultBlack
    };

    const { color1, color2, color1TextWhite, color1TextBlack, color2TextWhite, color2TextBlack } = args;

    const color1Set = getColorStyling({ color: color1, textWhite: color1TextWhite, textBlack: color1TextBlack });
    const color2Set = getColorStyling({ color: color2, textWhite: color2TextWhite, textBlack: color2TextBlack });
    const variablesToSet = {
      '--brand-color': color1Set.color.toHexString(),
      '--brand-color-contrast': color1Set.redeableColor.toHexString(),
      '--brand-color-shaded': color1Set.shaded.toHexString(),
      '--brand-color-shaded-constrast': color1Set.shadedRedeable.toHexString(),
      '--brand-color-hover': color1Set.hoverColor.toHexString(),
      '--brand-color-text-white': color1Set.textWhite.toHexString(),
      '--brand-color-text-black': color1Set.textBlack.toHexString(),
      '--background-color': color2Set.color.toHexString(),
      '--background-color-contrast': color2Set.redeableColor.toHexString(),
      '--background-color-shaded': color2Set.shaded.toHexString(),
      '--background-color-shaded-constrast': color2Set.shadedRedeable.toHexString(),
      '--background-color-hover': color2Set.hoverColor.toHexString(),
      '--background-color-text-white': color2Set.textWhite.toHexString(),
      '--background-color-text-black': color2Set.textBlack.toHexString(),
      '--brand-color-support': color1Set.redeableColorContrast?.toHexString() || '',
      '--brand-color-support-contrast': color1Set.redeableColorContrastInverse?.toHexString() || '',
      '--brand-color-support-shaded': color1Set.redeableColorContrast?.isLight()
        ? color1Set.redeableColorContrast.darken(5).toHexString()
        : color1Set.redeableColorContrast?.lighten(5).toHexString() || '',
      '--background-color-support': color2Set.redeableColorContrast?.toHexString() || '',
      '--background-color-support-contrast': color2Set.redeableColorContrastInverse?.toHexString() || '',
      '--border-radius': borderRadius,
      '--close-icon__color': color1Set.redeableColorContrastInverse?.toHexString() || '', // for header sidemenu
      //
      '--tmdb-filter': 'drop-shadow(0px 1000px 0 var(--background-color-contrast))'
    };
    this.headerService.setDisplayStyle('none');
    this.injectRootStyles(color2Set.color.toHexString());
    this.injectFooterColorStyles(
      color1Set.color.toHexString(),
      color1Set.redeableColor.toHexString(),
      color1Set.hoverColor.toHexString(),
      color2Set.color.toHexString(),
      color2Set.redeableColor.toHexString(),
      color2Set.hoverColor.toHexString(),
      color2Set.redeableColor.toHexString(),
      borderRadius
    );

    this.injectGoToTopStyles(
      color1Set.color.toHexString(),
      color1Set.redeableColor.toHexString(),
      color2Set.color.toHexString()
    );

    return variablesToSet;
  }

  private injectRootStyles(backgroundColor: string) {
    this.document.body.style.backgroundColor = backgroundColor;
  }

  private injectFooterColorStyles(
    brandColor: string,
    brandColorContrast: string,
    brandColorHover: string,
    backgroundColor: string,
    backgroundColorContrast: string,
    backgroundColorHover: string,
    textColor: string,
    borderRadius: string
  ) {
    if (!this.isPlatformBrowser) return;
    if (!this.injectStylesEl) return;
    const isBrandColorReadableOverBackground = isReadable(new TinyColor(brandColor), new TinyColor(backgroundColor));

    this.injectStylesEl.innerHTML += `
      .movie-branded-page-site ush-footer {
        --brandColor: ${brandColor};
        --brandColorContrast: ${brandColorContrast};
        --brandColorHover: ${brandColorHover};
        --backgroundColor: ${backgroundColor};
        --backgroundColorContrast: ${backgroundColorContrast};
        --backgroundColorHover: ${backgroundColorHover};
        --textColor: ${textColor};
        --borderRadius: ${borderRadius};
        --footerBackground: var(--backgroundColor);
        --subscribeBackground: var(--backgroundColor);
        --subscribeTitleColor: var(--textColor);
        --subscribeDescColor: var(--textColor);
        --footerLinksColor: var(--textColor);
        --dropdownTriggerBackgroundColor: var(--brandColor);
        --dropdownTriggerColor: var(--brandColorContrast);
      }
      .movie-branded-page-site ush-footer  .social-link {
        --socialLinksColor: ${isBrandColorReadableOverBackground ? 'var(--brandColor)' : 'var(--backgroundColorContrast)'};
      }
      .movie-branded-page-site ush-footer .button-secondary {
        background-color: var(--brandColor);
        color: var(--brandColorContrast);
        border: 2px solid var(--brandColor);
      }
      .movie-branded-page-site ush-footer .button-secondary:is(:hover, :focus) {
        background-color: var(--brandColorHover);
        color: var(--brandColorContrast);
        border: 2px solid var(--brandColor);
      }
    `;
  }

  injectCustomFooterStyle(styles: string) {
    if (!this.isPlatformBrowser) return;
    if (!this.injectStylesEl) return;
    this.injectStylesEl.innerHTML += styles;
  }

  private injectGoToTopStyles(brandColor: string, brandColorContrast: string, backgroundColor: string) {
    if (!this.isPlatformBrowser) return;
    if (!this.injectStylesEl) return;
    this.injectStylesEl.innerHTML += `
      .movie-branded-page-site #goToTop {
        --brandColor: ${brandColor};
        --brandColorContrast: ${brandColorContrast};
        --backgroundColor: ${backgroundColor};
        color: var(--brandColorContrast) !important;
      }
    `;
  }

  removeStyles() {
    if (!this.isNavigatingInsideMovieSite()) {
      this.headerService.resetDisplayStyle();
    }
    if (this.sidemenuRef) {
      this.sidemenuRef = undefined;
    }

    this.document.body.classList.remove(this.BODY_CLASS_NAME);
    this.document.body.style.removeProperty('background-color');

    if (this.injectStylesEl) {
      this.injectStylesEl.remove();
      this.injectStylesEl = undefined;
    }
  }

  private isNavigatingInsideMovieSite(): boolean {
    const navigation = this.router.getCurrentNavigation();
    const targetUrl = navigation?.finalUrl?.toString() || navigation?.extractedUrl?.toString() || '';

    return ['/film-info', '/book-now', '/watch-now', '/competition'].some(segment => targetUrl.includes(segment));
  }

  setFontsStyles(fontFamily: string, fontFamilyBold: string) {
    if (!this.injectStylesEl) return;
    const regularFount = `https://cdnstatic.usheru.com/fonts/${fontFamily}`;
    const boldFont = `https://cdnstatic.usheru.com/fonts/${fontFamilyBold}`;
    const getFontFormat = (url: string) => {
      const extension = url.split('.').pop();
      switch (extension) {
        case 'woff':
          return 'woff';
        case 'woff2':
          return 'woff2';
        case 'ttf':
          return 'truetype';
        case 'otf':
          return 'opentype';
        case 'eot':
          return 'embedded-opentype';
        default:
          return '';
      }
    };

    this.injectStylesEl.innerHTML += `
      @font-face {
        font-family: 'movie-font';
        src: url('${regularFount}') format('${getFontFormat(regularFount)}');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'movie-font';
        src: url('${boldFont}') format('${getFontFormat(boldFont)}');
        font-weight: 700;
        font-style: bold;
      }
    `;
  }

  moveFooter(refElement: HTMLElement) {
    if (!this.isPlatformBrowser) return;
    /**
     * In cases that the app has a custom footer. This footer must have the
     * attribute data-custom-footer.
     */
    const footer = (document.querySelector('ush-footer') ||
      document.querySelector('[data-custom-footer]')) as HTMLElement;
    if (!footer) return;
    const parent = footer?.parentElement as HTMLElement;
    let sibling, insertPostion: 'after' | 'before';
    const nextSibling = footer?.nextElementSibling as HTMLElement;
    if (nextSibling) {
      sibling = nextSibling;
      insertPostion = 'before';
    }
    const previousSibling = footer?.previousElementSibling as HTMLElement;
    if (previousSibling) {
      sibling = previousSibling;
      insertPostion = 'after';
    }
    if (!nextSibling && !previousSibling) {
      throw new Error('Footer has no sibling to be moved');
    }
    this.footerRestoreRef = { footer, parent, sibling: sibling!, insertPostion: insertPostion! };
    refElement.appendChild(footer);
  }

  restoreFooter() {
    if (!this.isPlatformBrowser) return;
    if (!this.footerRestoreRef) return;

    const { footer, parent, sibling, insertPostion } = this.footerRestoreRef;
    if (!footer || !parent || !sibling || !insertPostion) return;
    if (this.footerRestoreRef.insertPostion === 'after') {
      this.footerRestoreRef?.parent.insertBefore(
        this.footerRestoreRef.footer,
        this.footerRestoreRef.sibling.nextSibling
      );
    } else {
      this.footerRestoreRef?.parent.insertBefore(this.footerRestoreRef.footer, this.footerRestoreRef.sibling);
    }
  }

  checkAnchor() {
    const fragment = this.route.snapshot.fragment;
    if (fragment) {
      const section = document.getElementById(fragment) as HTMLElement;
      if (!section) return;
      this.goToSection(section, 'instant');
    }
  }

  goToSection(
    section: HTMLElement,
    behavior: ScrollBehavior = 'smooth',
    tracking?: {
      section: string;
      movie: Movie;
    }
  ) {
    if (!this.isPlatformBrowser) return;
    if (tracking) {
      const { section, movie } = tracking;
      this.trackingService.trackEvent({
        action: 'MovieMenu',
        properties: {
          category: 'MovieDetail',
          label: section,
          movie: movie.title || movie.friendlyUrl,
          idMovie: movie.id,
          imdb: movie?.imdb,
          tagsMovie: getMovieTags(movie),
          genresMovie: getMovieGenres(movie)
        }
      });
    }
    if (section) section.scrollIntoView({ behavior });
    this.closeSidemenu();
  }

  readonly checkMovieCompetition = (movie?: Movie | null): boolean => {
    return (
      movie?.properties?.competition?.typeFormEnabled === true &&
      !!movie?.properties?.competition?.typeFormUrl &&
      !!movie?.properties?.competition?.typeFormData
    );
  };

  getCompetitionPlacement(movie?: Movie | null): MovieSiteCompetitionPlacement {
    const configuredPlacement = getChannelMovieProp(
      'movieSite.competition.placement',
      this.channelService.channel,
      movie
    );

    if (this.isCompetitionPlacement(configuredPlacement)) {
      return configuredPlacement;
    }

    return this.defaultCompetitionPlacement;
  }

  shouldShowCompetitionInAwareness(movie?: Movie | null): boolean {
    if (!this.checkMovieCompetition(movie)) {
      return false;
    }

    const placement = this.getCompetitionPlacement(movie);
    return placement === 'awareness' || placement === 'both';
  }

  shouldShowCompetitionInConversion(movie?: Movie | null): boolean {
    if (!this.checkMovieCompetition(movie)) {
      return false;
    }

    const placement = this.getCompetitionPlacement(movie);
    return placement === 'conversion' || placement === 'both';
  }

  private isCompetitionPlacement(value: unknown): value is MovieSiteCompetitionPlacement {
    return this.competitionPlacementValues.includes(value as MovieSiteCompetitionPlacement);
  }

  closeSidemenu() {
    if (this.sidemenuRef) this.sidemenuRef.close();
  }

  hexToRgb(hex: string): number[] | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
  }

  get isDesktop() {
    return window.innerWidth >= this.desktopBreakpoint;
  }
}

interface ColorSetInput {
  color: TinyColor;
  textWhite: TinyColor;
  textBlack: TinyColor;
}

interface ColorSet {
  color: TinyColor;
  redeableColor: TinyColor;
  hoverColor: TinyColor;
  textWhite: TinyColor;
  textBlack: TinyColor;
  /**
   * Variant more darker or lighter of the color
   */
  shaded: TinyColor;
  shadedRedeable: TinyColor;
  /**
   * This color must be black or white. Used as support color for some contrast cases.
   */
  redeableColorContrast?: TinyColor;
  /**
   * This color must be black or white. Used as support color for some contrast cases.
   */
  redeableColorContrastInverse?: TinyColor;
}
